import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useMicrosoftAuth } from '@/hooks/useMicrosoftAuth';
import { useUIStore } from '@/stores/uiStore';
import { useValuationRequestCount } from '@/hooks/useValuationRequests';
import { useOMCounts } from '@/hooks/useOfferingMemorandums';
import { Avatar } from '@/components/ui/Avatar';
import { FlowMark } from '@/components/ui/FlowMark';
import { Tooltip } from '@/components/ui/Tooltip';
import { UserMenuDropdown } from '@/components/user/UserMenuDropdown';
import type { Profile } from '@/types/database';
import {
  ChevronRight,
  ChevronsUpDown,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  X,
} from 'lucide-react';
import { preloadRoute } from '@/lib/routePreload';
import { NAVIGATION, isNavItemActive, type NavItem } from './navigation';
import { emitShellEvent } from './shellEvents';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

/** "⌘K" on a Mac, "Ctrl K" everywhere else. */
const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);

function CountBadge({ count, className }: { count: number; className?: string }) {
  return (
    <span
      className={cn(
        'flex items-center justify-center rounded-full bg-primary-500 font-semibold tabular-nums text-white',
        className
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function SidebarLink({
  item,
  active,
  collapsed,
  badgeCount,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  badgeCount: number;
}) {
  const showBadge = badgeCount > 0;

  const link = (
    <NavLink
      to={item.href}
      // Start downloading the page while the pointer is on its way to a click
      // (pages load on demand — src/lib/lazyPage.ts). Focus covers the keyboard.
      onMouseEnter={() => preloadRoute(item.href)}
      onFocus={() => preloadRoute(item.href)}
      onTouchStart={() => preloadRoute(item.href)}
      // The active state is this sidebar's own prefix rule (isNavItemActive),
      // not NavLink's, so aria-current is set from it too.
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      className={cn(
        'group relative flex items-center rounded-lg text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/70',
        collapsed ? 'h-9 w-9 justify-center' : 'gap-3 px-3 py-2',
        active
          ? 'bg-white/[0.12] text-white'
          : 'text-chrome-muted hover:bg-white/[0.07] hover:text-white'
      )}
    >
      <span className="relative flex-shrink-0">
        <item.icon
          className={cn(
            'h-4 w-4 transition-colors',
            active ? 'text-primary-400' : 'text-chrome-muted group-hover:text-white'
          )}
          aria-hidden="true"
        />
        {showBadge && collapsed && (
          <CountBadge
            count={badgeCount}
            className="absolute -right-2 -top-2 h-4 min-w-4 px-1 text-[10px] ring-2 ring-chrome"
          />
        )}
      </span>
      {!collapsed && (
        <>
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {showBadge && <CountBadge count={badgeCount} className="h-5 min-w-5 px-1.5 text-[11px]" />}
        </>
      )}
    </NavLink>
  );

  // The label is only needed while the sidebar shows bare icons.
  return (
    <Tooltip content={item.label} side="right" sideOffset={10} disabled={!collapsed}>
      {link}
    </Tooltip>
  );
}

export function Sidebar({ collapsed: collapsedPreference, onToggle }: SidebarProps) {
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const { user: msUser, logout: msLogout, isAuthenticated: isMsAuthenticated } = useMicrosoftAuth();
  const mobileMenuOpen = useUIStore((s) => s.mobileMenuOpen);
  const setMobileMenuOpen = useUIStore((s) => s.setMobileMenuOpen);
  const collapsedSections = useUIStore((s) => s.collapsedNavSections);
  const toggleNavSection = useUIStore((s) => s.toggleNavSection);
  const expandNavSection = useUIStore((s) => s.expandNavSection);
  const { data: valuationCount = 0 } = useValuationRequestCount();
  const { data: omCounts = {} } = useOMCounts();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);

  // The phone drawer always shows labels: "collapsed" is a desktop preference
  // about width, and a drawer of bare icons would be the worst of both.
  const collapsed = collapsedPreference && !mobileMenuOpen;

  // Total of all new inbound items: valuation requests + new OMs
  const newLeadsCount = valuationCount + ((omCounts as Record<string, number>).new || 0);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname, setMobileMenuOpen]);

  // Growing the window to desktop width while the drawer is open would leave
  // it open over a sidebar that is now permanently visible.
  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const onChange = () => {
      if (desktop.matches) setMobileMenuOpen(false);
    };
    desktop.addEventListener('change', onChange);
    return () => desktop.removeEventListener('change', onChange);
  }, [setMobileMenuOpen]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen, setMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Arriving on a page inside a folded section unfolds it: the place you are
  // should never be hidden from the list of places.
  useEffect(() => {
    const owner = NAVIGATION.find((section) =>
      section.items.some((item) => isNavItemActive(location.pathname, item.href))
    );
    if (owner) expandNavSection(owner.id);
  }, [location.pathname, expandNavSection]);

  const displayName = profile?.full_name || msUser?.name || user?.email || msUser?.email || 'Account';
  const displayEmail = user?.email || msUser?.email || null;
  const secondaryLine =
    displayEmail && displayEmail !== displayName
      ? displayEmail
      : profile?.role
        ? profile.role.replace(/_/g, ' ')
        : null;

  const handleSignOut = async () => {
    // Always sign out of both auth systems to prevent auto-login loop
    try {
      if (isMsAuthenticated) {
        await msLogout();
      }
    } catch (err) {
      console.warn('Microsoft logout failed:', err);
    }
    // signOut handles Supabase + clearing MSAL cache + redirect
    await signOut();
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden animate-fade-in-plain"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        aria-label="Primary"
        className={cn(
          // One dark surface with the top bar; the content panel's rounded
          // corner is the edge, so there is no border on the right.
          'fixed left-0 top-0 z-50 flex h-screen flex-col bg-chrome text-white supports-[height:100dvh]:h-dvh',
          'transition-[width,transform] duration-200 ease-out print:hidden',
          // Mobile: a drawer that slides in from the left
          'w-72',
          mobileMenuOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
          // Desktop: always present, at one of two widths
          'lg:translate-x-0 lg:shadow-none',
          collapsedPreference ? 'lg:w-16' : 'lg:w-60'
        )}
      >
        {/* Brand */}
        <div
          className={cn(
            'flex h-14 flex-shrink-0 items-center',
            collapsed ? 'justify-center px-2' : 'gap-2.5 pl-4 pr-2'
          )}
        >
          <FlowMark size={28} title={collapsed ? 'Flow' : null} />
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-bold leading-tight text-white">Flow</div>
                <div className="truncate text-[11px] leading-tight text-chrome-muted">
                  Elevation Capital Group
                </div>
              </div>
              {/* Collapse (desktop) */}
              <Tooltip content={`Collapse sidebar (${isMac ? '⌘' : 'Ctrl '}B)`} side="right">
                <button
                  type="button"
                  onClick={onToggle}
                  aria-label="Collapse sidebar"
                  className="hidden h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-chrome-muted transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/70 lg:flex"
                >
                  <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
                </button>
              </Tooltip>
              {/* Close (mobile drawer) */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-chrome-muted transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/70 lg:hidden"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        {/* Search */}
        <div className={cn('flex-shrink-0 pb-2', collapsed ? 'flex justify-center px-2' : 'px-3')}>
          {collapsed ? (
            <Tooltip content={`Search (${isMac ? '⌘' : 'Ctrl '}K)`} side="right" sideOffset={10}>
              <button
                type="button"
                onClick={() => emitShellEvent('open-command-palette')}
                aria-label="Search"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-chrome-muted transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/70"
              >
                <Search className="h-4 w-4" aria-hidden="true" />
              </button>
            </Tooltip>
          ) : (
            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(false);
                emitShellEvent('open-command-palette');
              }}
              className="flex h-9 w-full items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 text-sm text-chrome-muted transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/70"
            >
              <Search className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span className="flex-1 text-left">Search…</span>
              <kbd className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 font-sans text-[11px] font-medium text-chrome-muted">
                {isMac ? '⌘K' : 'Ctrl K'}
              </kbd>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav
          aria-label="Main"
          className={cn(
            'min-h-0 flex-1 overflow-y-auto overscroll-contain pb-3 scrollbar-hidden',
            collapsed ? 'px-2' : 'px-2'
          )}
        >
          {NAVIGATION.map((section, sectionIndex) => {
            const folded = !collapsed && !!section.title && collapsedSections.includes(section.id);
            const hasActiveChild = section.items.some((item) =>
              isNavItemActive(location.pathname, item.href)
            );
            const sectionDomId = `nav-section-${section.id}`;

            return (
              <div key={section.id} className={cn(sectionIndex > 0 && collapsed && 'mt-2 border-t border-white/10 pt-2')}>
                {section.title && !collapsed && (
                  <button
                    type="button"
                    onClick={() => toggleNavSection(section.id)}
                    aria-expanded={!folded}
                    aria-controls={sectionDomId}
                    className="group/header flex w-full items-center gap-1 rounded-md px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-widest text-chrome-muted/70 transition-colors hover:text-white focus-visible:outline-none focus-visible:text-white"
                  >
                    <ChevronRight
                      className={cn(
                        'h-3 w-3 flex-shrink-0 transition-transform duration-200',
                        !folded && 'rotate-90'
                      )}
                      aria-hidden="true"
                    />
                    <span className="flex-1 text-left">{section.title}</span>
                    {folded && hasActiveChild && (
                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-500" />
                    )}
                  </button>
                )}
                <div
                  id={sectionDomId}
                  // A folded section is removed from the tab order and the
                  // accessibility tree, not just made zero-height.
                  hidden={folded}
                  className={cn('space-y-0.5', collapsed && 'flex flex-col items-center')}
                >
                  {section.items.map((item) => (
                    <SidebarLink
                      key={item.href}
                      item={item}
                      active={isNavItemActive(location.pathname, item.href)}
                      collapsed={collapsed}
                      badgeCount={item.href === '/pipeline/inbound-leads' ? newLeadsCount : 0}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Account */}
        <div className={cn('flex-shrink-0 border-t border-white/10 p-2', collapsed && 'flex flex-col items-center gap-1')}>
          {collapsed ? (
            <Tooltip content={displayName} side="right" sideOffset={10}>
              <button
                ref={userMenuButtonRef}
                type="button"
                onClick={() => setIsUserMenuOpen((open) => !open)}
                aria-label="Account menu"
                aria-haspopup="menu"
                aria-expanded={isUserMenuOpen}
                className="flex h-9 w-9 items-center justify-center rounded-lg transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/70"
              >
                <Avatar src={profile?.avatar_url} name={displayName} size="xs" variant="solid" />
              </button>
            </Tooltip>
          ) : (
            <button
              ref={userMenuButtonRef}
              type="button"
              onClick={() => setIsUserMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={isUserMenuOpen}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/70',
                isUserMenuOpen ? 'bg-white/10' : 'hover:bg-white/[0.07]'
              )}
            >
              <Avatar
                src={profile?.avatar_url}
                name={displayName}
                size="sm"
                variant="solid"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium leading-tight text-white">
                  {displayName}
                </span>
                {secondaryLine && (
                  <span className="block truncate text-[11px] leading-tight text-chrome-muted">
                    {secondaryLine}
                  </span>
                )}
              </span>
              <ChevronsUpDown className="h-4 w-4 flex-shrink-0 text-chrome-muted/70" aria-hidden="true" />
            </button>
          )}

          {collapsed && (
            <Tooltip content={`Expand sidebar (${isMac ? '⌘' : 'Ctrl '}B)`} side="right" sideOffset={10}>
              <button
                type="button"
                onClick={onToggle}
                aria-label="Expand sidebar"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-chrome-muted transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/70"
              >
                <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
              </button>
            </Tooltip>
          )}
        </div>
      </aside>

      <UserMenuDropdown
        isOpen={isUserMenuOpen}
        onClose={() => setIsUserMenuOpen(false)}
        triggerRef={userMenuButtonRef}
        placement="top-start"
        user={user || (msUser ? { email: msUser.email } : null)}
        profile={
          profile ||
          (msUser ? ({ full_name: msUser.name, email: msUser.email } as unknown as Profile) : null)
        }
        onSignOut={handleSignOut}
      />
    </>
  );
}
